/**
 * Preset Registry
 *
 * The registry is the single source of truth for WHICH code implements each
 * preset slot. The backend decides WHICH preset the user sees; this file
 * decides HOW each slot is rendered.
 */
import { lazy } from "react";
import { classicNeonTheme, nocturneTheme } from "./themes/tokens";

// Shells
import ClassicStackShell from "./shells/ClassicStackShell";
import NocturneShell from "./shells/NocturneShell";
import NocturneAuthShell from "./shells/NocturneAuthShell";
import ClientV3Shell from "./shells/ClientV3Shell";

// Page variants are mutually exclusive at runtime. Keeping every variant as a
// static import made even a vendor storefront download and parse the complete
// legacy client catalogue before it could become interactive. React.lazy keeps
// the registry API unchanged while moving each variant behind its route.
const lazyVariant = (loader) => lazy(loader);

// --- Home ---
const HomeV1 = lazyVariant(() => import("./pageVariants/home/HomeV1"));
const HomeV2 = lazyVariant(() => import("./pageVariants/home/HomeV2"));
const HomeV3 = lazyVariant(() => import("./pageVariants/home/HomeV3"));
// --- Wallet ---
const WalletV1 = lazyVariant(() => import("./pageVariants/wallet/WalletV1"));
const WalletV2 = lazyVariant(() => import("./pageVariants/wallet/WalletV2"));
const WalletV3 = lazyVariant(() => import("./pageVariants/wallet/WalletV3"));
// --- Games ---
const GamesV1 = lazyVariant(() => import("./pageVariants/games/GamesV1"));
const GamesV2 = lazyVariant(() => import("./pageVariants/games/GamesV2"));
const GamesV3 = lazyVariant(() => import("./pageVariants/games/GamesV3"));
// --- Game Detail ---
const GameDetailV1 = lazyVariant(() => import("./pageVariants/gameDetail/GameDetailV1"));
const GameDetailV2 = lazyVariant(() => import("./pageVariants/gameDetail/GameDetailV2"));
const GameDetailV3 = lazyVariant(() => import("./pageVariants/gameDetail/GameDetailV3"));
// --- Profile ---
const ProfileV1 = lazyVariant(() => import("./pageVariants/profile/ProfileV1"));
const ProfileV2 = lazyVariant(() => import("./pageVariants/profile/ProfileV2"));
const ProfileV3 = lazyVariant(() => import("./pageVariants/profile/ProfileV3"));
// --- Referrals ---
const ReferralsV1 = lazyVariant(() => import("./pageVariants/referrals/ReferralsV1"));
const ReferralsV2 = lazyVariant(() => import("./pageVariants/referrals/ReferralsV2"));
// --- Rewards ---
const RewardsV1 = lazyVariant(() => import("./pageVariants/rewards/RewardsV1"));
const RewardsV2 = lazyVariant(() => import("./pageVariants/rewards/RewardsV2"));
// --- Messages ---
const MessagesV1 = lazyVariant(() => import("./pageVariants/messages/MessagesV1"));
const MessagesV2 = lazyVariant(() => import("./pageVariants/messages/MessagesV2"));
// --- Inbox ---
const InboxV1 = lazyVariant(() => import("./pageVariants/inbox/InboxV1"));
const InboxV2 = lazyVariant(() => import("./pageVariants/inbox/InboxV2"));
const InboxV3 = lazyVariant(() => import("./pageVariants/inbox/InboxV3"));
// --- Transactions ---
const TransactionsV1 = lazyVariant(() => import("./pageVariants/transactions/TransactionsV1"));
const TransactionsV2 = lazyVariant(() => import("./pageVariants/transactions/TransactionsV2"));
// --- Verification ---
const VerificationV1 = lazyVariant(() => import("./pageVariants/verification/VerificationV1"));
const VerificationV2 = lazyVariant(() => import("./pageVariants/verification/VerificationV2"));
// --- Landing ---
const LandingV1 = lazyVariant(() => import("./pageVariants/landing/LandingV1"));
const LandingV2 = lazyVariant(() => import("./pageVariants/landing/LandingV2"));
const LandingV3 = lazyVariant(() => import("./pageVariants/landing/LandingV3"));
// --- Auth ---
const LoginV1 = lazyVariant(() => import("./pageVariants/auth/LoginV1"));
const LoginV2 = lazyVariant(() => import("./pageVariants/auth/LoginV2"));
const LoginV3 = lazyVariant(() => import("./pageVariants/auth/LoginV3"));
const SignupV1 = lazyVariant(() => import("./pageVariants/auth/SignupV1"));
const SignupV2 = lazyVariant(() => import("./pageVariants/auth/SignupV2"));
const SignupV3 = lazyVariant(() => import("./pageVariants/auth/SignupV3"));
const ForgotV1 = lazyVariant(() => import("./pageVariants/auth/ForgotV1"));
const ForgotV2 = lazyVariant(() => import("./pageVariants/auth/ForgotV2"));
const MagicLinkV1 = lazyVariant(() => import("./pageVariants/auth/MagicLinkV1"));
const MagicLinkV2 = lazyVariant(() => import("./pageVariants/auth/MagicLinkV2"));
const SetPasswordV1 = lazyVariant(() => import("./pageVariants/auth/SetPasswordV1"));
const SetPasswordV2 = lazyVariant(() => import("./pageVariants/auth/SetPasswordV2"));
// --- Rules / Legal (C4 article wraps) ---
const RulesHowV1 = lazyVariant(() => import("./pageVariants/rules/RulesHowV1"));
const RulesHowV2 = lazyVariant(() => import("./pageVariants/rules/RulesHowV2"));
const RulesReferralsV1 = lazyVariant(() => import("./pageVariants/rules/RulesReferralsV1"));
const RulesReferralsV2 = lazyVariant(() => import("./pageVariants/rules/RulesReferralsV2"));
const RulesTiersV1 = lazyVariant(() => import("./pageVariants/rules/RulesTiersV1"));
const RulesTiersV2 = lazyVariant(() => import("./pageVariants/rules/RulesTiersV2"));
const RulesBonusesV1 = lazyVariant(() => import("./pageVariants/rules/RulesBonusesV1"));
const RulesBonusesV2 = lazyVariant(() => import("./pageVariants/rules/RulesBonusesV2"));
const RulesPaymentsV1 = lazyVariant(() => import("./pageVariants/rules/RulesPaymentsV1"));
const RulesPaymentsV2 = lazyVariant(() => import("./pageVariants/rules/RulesPaymentsV2"));
const TermsV1 = lazyVariant(() => import("./pageVariants/terms/TermsV1"));
const TermsV2 = lazyVariant(() => import("./pageVariants/terms/TermsV2"));
const PrivacyV1 = lazyVariant(() => import("./pageVariants/privacy/PrivacyV1"));
const PrivacyV2 = lazyVariant(() => import("./pageVariants/privacy/PrivacyV2"));

// ------------------------------------------------------------
// Themes
// ------------------------------------------------------------
export const THEMES = {
    classic_neon: classicNeonTheme,
    nocturne: nocturneTheme,
};

// ------------------------------------------------------------
// Shells (main + optional auth shell per preset)
// ------------------------------------------------------------
export const SHELLS = {
    classic_stack_bottomnav: ClassicStackShell,
    nocturne: NocturneShell,
    client_v3: ClientV3Shell,
};

// A preset id → optional "auth shell" for auth / landing / rules / terms / privacy.
// If not defined, those routes render bare (Preset 1 behaviour).
const AUTH_SHELLS_BY_PRESET = {
    preset_2: NocturneAuthShell,
};

// ------------------------------------------------------------
// Variants
// ------------------------------------------------------------
export const VARIANTS = {
    home:              { home_v1: HomeV1, home_v2: HomeV2, home_v3: HomeV3 },
    wallet:            { wallet_v1: WalletV1, wallet_v2: WalletV2, wallet_v3: WalletV3 },
    games:             { games_v1: GamesV1, games_v2: GamesV2, games_v3: GamesV3 },
    game_detail:       { game_detail_v1: GameDetailV1, game_detail_v2: GameDetailV2, game_detail_v3: GameDetailV3 },
    profile:           { profile_v1: ProfileV1, profile_v2: ProfileV2, profile_v3: ProfileV3 },
    referrals:         { referrals_v1: ReferralsV1, referrals_v2: ReferralsV2 },
    rewards:           { rewards_v1: RewardsV1, rewards_v2: RewardsV2 },
    messages:          { messages_v1: MessagesV1, messages_v2: MessagesV2 },
    inbox:             { inbox_v1: InboxV1, inbox_v2: InboxV2, inbox_v3: InboxV3 },
    transactions:      { transactions_v1: TransactionsV1, transactions_v2: TransactionsV2 },
    verification:      { verification_v1: VerificationV1, verification_v2: VerificationV2 },
    landing:           { landing_v1: LandingV1, landing_v2: LandingV2, landing_v3: LandingV3 },
    auth_login:        { auth_login_v1: LoginV1, auth_login_v2: LoginV2, auth_login_v3: LoginV3 },
    auth_signup:       { auth_signup_v1: SignupV1, auth_signup_v2: SignupV2, auth_signup_v3: SignupV3 },
    auth_forgot:       { auth_forgot_v1: ForgotV1, auth_forgot_v2: ForgotV2 },
    auth_magic:        { auth_magic_v1: MagicLinkV1, auth_magic_v2: MagicLinkV2 },
    auth_setpassword:  { auth_setpassword_v1: SetPasswordV1, auth_setpassword_v2: SetPasswordV2 },
    rules_how:         { rules_how_v1: RulesHowV1, rules_how_v2: RulesHowV2 },
    rules_referrals:   { rules_referrals_v1: RulesReferralsV1, rules_referrals_v2: RulesReferralsV2 },
    rules_tiers:       { rules_tiers_v1: RulesTiersV1, rules_tiers_v2: RulesTiersV2 },
    rules_bonuses:     { rules_bonuses_v1: RulesBonusesV1, rules_bonuses_v2: RulesBonusesV2 },
    rules_payments:    { rules_payments_v1: RulesPaymentsV1, rules_payments_v2: RulesPaymentsV2 },
    terms:             { terms_v1: TermsV1, terms_v2: TermsV2 },
    privacy:           { privacy_v1: PrivacyV1, privacy_v2: PrivacyV2 },
};

// Preset 3 — Client UI V3 (used by multi-vendor client portal at /v/:slug)
export const PRESET_V3 = {
    id: "preset_3",
    slug: "preset_3",
    name: "Client UI V3",
    theme_id: "classic_neon",
    shell_id: "client_v3",
    page_config: {
        home: { variant: "home_v3" },
        wallet: { variant: "wallet_v3" },
        games: { variant: "games_v3" },
        game_detail: { variant: "game_detail_v3" },
        profile: { variant: "profile_v3" },
        inbox: { variant: "inbox_v3" },
        landing: { variant: "landing_v3" },
        auth_login: { variant: "auth_login_v3" },
        auth_signup: { variant: "auth_signup_v3" },
    },
};

// ------------------------------------------------------------
// Fallback preset (used if /api/appearance/me ever 5xx's)
// ------------------------------------------------------------
export const FALLBACK_PRESET = {
    id: "preset_1",
    slug: "preset_1",
    name: "Preset 1",
    theme_id: "classic_neon",
    shell_id: "classic_stack_bottomnav",
    page_config: {
        home: { variant: "home_v1" },
        wallet: { variant: "wallet_v1" },
        games: { variant: "games_v1" },
        game_detail: { variant: "game_detail_v1" },
        profile: { variant: "profile_v1" },
        referrals: { variant: "referrals_v1" },
        rewards: { variant: "rewards_v1" },
        messages: { variant: "messages_v1" },
        inbox: { variant: "inbox_v1" },
        transactions: { variant: "transactions_v1" },
        verification: { variant: "verification_v1" },
        landing: { variant: "landing_v1" },
        auth_login: { variant: "auth_login_v1" },
        auth_signup: { variant: "auth_signup_v1" },
        auth_forgot: { variant: "auth_forgot_v1" },
        auth_magic: { variant: "auth_magic_v1" },
        auth_setpassword: { variant: "auth_setpassword_v1" },
        rules_how: { variant: "rules_how_v1" },
        rules_referrals: { variant: "rules_referrals_v1" },
        rules_tiers: { variant: "rules_tiers_v1" },
        rules_bonuses: { variant: "rules_bonuses_v1" },
        rules_payments: { variant: "rules_payments_v1" },
        terms: { variant: "terms_v1" },
        privacy: { variant: "privacy_v1" },
    },
};

// ------------------------------------------------------------
// Resolvers
// ------------------------------------------------------------
export function resolveTheme(themeId) {
    return THEMES[themeId] || THEMES.classic_neon;
}

export function resolveShell(shellId) {
    return SHELLS[shellId] || SHELLS.classic_stack_bottomnav;
}

export function resolveAuthShell(presetId) {
    return AUTH_SHELLS_BY_PRESET[presetId] || null;
}

export function presetSupportsAuthShell(presetId) {
    return !!AUTH_SHELLS_BY_PRESET[presetId];
}

export function resolveVariant(slot, variantId) {
    const slotMap = VARIANTS[slot];
    if (!slotMap) return null;
    if (variantId && slotMap[variantId]) return slotMap[variantId];
    const fallbackKey = `${slot}_v1`;
    return slotMap[fallbackKey] || Object.values(slotMap)[0] || null;
}

export function getRegistryMeta() {
    return {
        themes: Object.keys(THEMES),
        shells: Object.keys(SHELLS),
        variants: Object.fromEntries(
            Object.entries(VARIANTS).map(([slot, map]) => [slot, Object.keys(map)]),
        ),
    };
}
